import { Plus, FloppyDisk } from 'phosphor-react'
import { useCallback, useEffect, useState } from 'react'
import { ContentLoading } from '../../../../components/ContentLoading'
import apinode from '../../../../services/apinode'
import { sendErrorApiMessage } from '../../../../utils/sendErrorApiMessage'
import { CustomSpinner } from '../../../../components/CustomSpinner'
import { toast } from 'react-toastify'
import { unifyParams } from '../../../../utils/unifyParams'
import { differenceInDays, format } from 'date-fns'
import { useOrdenation } from '../../../../hooks/useOrdenation'
import Navbar from '../../../../components/navbar/navbar'
import { useLocation } from 'react-router-dom'

// ─── Icons ───────────────────────────────────────────────────────────────────

const IconEdit = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const IconTrash = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4h6v2" />
  </svg>
)

const IconCheck = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fieldLabel = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.8px',
  color: '#888',
  textTransform: 'uppercase',
  marginBottom: 7,
}

const fieldInput = {
  width: '100%',
  padding: '9px 14px',
  border: '1px solid #ddd',
  borderRadius: 8,
  fontSize: 13,
  color: '#1a1a1a',
  background: '#fafaf8',
  outline: 'none',
  boxSizing: 'border-box',
}

const fieldSelect = {
  ...fieldInput,
  cursor: 'pointer',
}

const emptyForm = {
  idpedidoloja: '',
  pedido_matriz: '',
  cliente: '',
  fornecedor: '',
  status: '',
  dataPedido: '',
  dataEntrega: '',
  dataArmazenado: '',
  cidade: '',
  observacao: '',
}

const emptyProduto = {
  codigo: '',
  descricao: '',
  qtd: '',
  substatus: '',
  obs: '',
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const statusColors = {
  'ARMAZENADO COM FALTA': { bg: '#fee2e2', color: '#991b1b' },
  'AGUARDANDO SEPARAÇÃO': { bg: '#fef3c7', color: '#92400e' },
  'ARMAZENADO COMPLETO': { bg: '#d1fae5', color: '#065f46' },
  ENTREGUE: { bg: '#ede9fe', color: '#5b21b6' },
}

const StatusBadge = ({ label }) => {
  const style = statusColors[label] || { bg: '#f0ede8', color: '#444' }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        background: style.bg,
        color: style.color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────

export function LojaPedidos() {
  const [pedidosList, setPedidosList] = useState([])
  const [statusOptions, setStatusOptions] = useState([])
  const [substatusOptions, setSubstatusOptions] = useState([])
  const [fornecedoresOptions, setFornecedoresOptions] = useState([])
  const [isSearchItemLoading, setIsSearchItemLoading] = useState({})
  const [isFetchItemLoading, setIsFetchItemLoading] = useState(false)

  const { state } = useLocation()

  // Filtros
  const [filtros, setFiltros] = useState({
    cliente: '',
    pedido: '',
    status: '',
    dataInicio: '',
    dataFim: '',
    cidade: '',
  })

  // Modal pedido (novo/editar)
  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [produtos, setProdutos] = useState([])
  const [buscaPedido, setBuscaPedido] = useState('')

  // Modal alterar status
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [statusModalId, setStatusModalId] = useState(null)
  const [novoStatus, setNovoStatus] = useState('')

  // Loading states
  const [isFetchLoading, setIsFetchLoading] = useState(true)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [isBuscaLoading, setIsBuscaLoading] = useState(false)
  const [isDeleteLoading, setIsDeleteLoading] = useState({})
  const [isStatusLoading, setIsStatusLoading] = useState(false)

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const { filteredItems, Th, Paginate } = useOrdenation(
    pedidosList,
    'data_pedido',
    'desc',
  )

  async function handleSearchItem(sku, index) {
    if (sku) {
      try {
        setIsSearchItemLoading((prevState) => ({
          ...prevState,
          [index]: true,
        }))

        const response = await apinode.get(
          `/loja/pedidos/produto/sku/${localStorage.idconta}?sku=${sku}`,
        )

        setProdutos((prevState) =>
          prevState.with(index, {
            ...prevState[index],
            descricao: response.data?.descricao,
          }),
        )
      } catch (err) {
        sendErrorApiMessage(err, 'Erro ao buscar o item')
      } finally {
        setIsSearchItemLoading((prevState) => ({
          ...prevState,
          [index]: false,
        }))
      }
    }
  }

  async function fetchData() {
    try {
      setIsFetchLoading(true)

      const url = unifyParams(`/loja/pedidos/${localStorage.idconta}`, [
        {
          key: 'cliente',
          value: filtros.cliente,
        },
        {
          key: 'pedido',
          value: filtros.pedido,
        },
        {
          key: 'status',
          value: filtros.status,
        },
        {
          key: 'data_ini',
          value: filtros.dataInicio,
        },
        {
          key: 'data_fim',
          value: filtros.dataFim,
        },
        {
          key: 'cidade',
          value: filtros.cidade,
        },
      ])

      const response = await apinode.get(url)

      setPedidosList(response.data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar pedidos')
    } finally {
      setIsFetchLoading(false)
    }
  }

  const fetchOptions = useCallback(async () => {
    try {
      const [statusRes, substatusRes] = await Promise.all([
        apinode.get(`/loja/status/${localStorage.idconta}?tipo=S`),
        apinode.get(`/loja/status/${localStorage.idconta}?tipo=U`),
      ])

      setStatusOptions(statusRes.data)
      setSubstatusOptions(substatusRes.data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar opções')
    }
  }, [])

  async function fetchFornecedores() {
    try {
      const response = await apinode.get(
        `/loja/fornecedores/${localStorage.idconta}`,
      )

      setFornecedoresOptions(response.data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar fornecedores')
    }
  }

  // ── Buscar pedido existente ────────────────────────────────────────────────

  const handleBuscarPedido = async () => {
    if (!buscaPedido.trim()) return

    try {
      setIsBuscaLoading(true)

      const response = await apinode.get(
        `/loja/pedidos/buscar/${localStorage.idconta}?termo=${buscaPedido}`,
      )

      const pedido = response.data

      setForm({
        pedido_matriz: pedido.pedido_matriz ?? '',
        idpedidoloja: pedido.idpedidoloja ?? '',
        cliente: pedido.cliente ?? '',
        dataPedido: pedido.data
          ? format(new Date(pedido.data), 'yyyy-MM-dd')
          : '',
      })
    } catch (err) {
      sendErrorApiMessage(err, 'Pedido não encontrado')
    } finally {
      setIsBuscaLoading(false)
    }
  }

  // ── CRUD pedido ────────────────────────────────────────────────────────────

  const openNew = () => {
    setForm(emptyForm)
    setProdutos([])
    setEditId(null)
    setBuscaPedido('')
    setModalOpen(true)
  }

  const openEdit = async (id) => {
    try {
      setIsFetchItemLoading(true)
      setModalOpen(true)

      const response = await apinode.get(
        `/loja/pedidos/${id}/${localStorage.idconta}`,
      )

      const { data, itens } = response.data

      setEditId(id)
      setBuscaPedido('')

      setForm({
        idpedidoloja: data.idpedidoloja ?? '',
        pedido_matriz: data.pedido_matriz ?? '',
        status: data.id_status ?? '',
        fornecedor: data.fornecedor ?? '',
        cliente: data.cliente ?? '',
        dataPedido: data?.data_pedido
          ? format(new Date(data.data_pedido), 'yyyy-MM-dd')
          : '',
        dataEntrega: data?.data_entrega
          ? format(new Date(data.data_entrega), 'yyyy-MM-dd')
          : '',
        dataArmazenado: data?.data_armazenado
          ? format(new Date(data.data_armazenado), 'yyyy-MM-dd')
          : '',
        cidade: data.cidade ?? '',
        observacao: data.observacao ?? '',
      })

      const itensFormatted = itens.map((item) => ({
        codigo: item.cod_item,
        descricao: item.descricao,
        qtd: item.quantidade,
        substatus: item.id_substatus,
        obs: item.observacao,
      }))

      setProdutos(itensFormatted ?? [])
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar informações')
    } finally {
      setIsFetchItemLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setIsUpdateLoading(true)

      const formattedPayload = {
        idpedidoloja: form.idpedidoloja,
        pedido_matriz: form.pedido_matriz,
        cliente: form.cliente,
        fornecedor: form.fornecedor,
        data_pedido: form.dataPedido,
        data_entrega: form.dataEntrega,
        data_armazenado: form.dataArmazenado,
        cidade: form.cidade,
        id_status: form.status,
        observacao: form.observacao,
        itens: produtos.map((produto) => ({
          cod_item: produto.codigo,
          descricao: produto.descricao,
          quantidade: produto.qtd,
          id_substatus: produto.substatus,
          observacao: produto.obs,
        })),
      }

      if (editId !== null) {
        await apinode.put(
          `/loja/pedidos/${editId}/${localStorage.idconta}`,
          formattedPayload,
        )
      } else {
        await apinode.post(
          `/loja/pedidos/${localStorage.idconta}`,
          formattedPayload,
        )
      }

      toast.success('A operação foi realizada com sucesso')
      await fetchData()
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao realizar a operação')
    } finally {
      setIsUpdateLoading(false)
      setModalOpen(false)
    }
  }

  const handleDelete = async (id, index) => {
    if (window.confirm('Você deseja deletar este pedido?')) {
      try {
        setIsDeleteLoading((prev) => ({ ...prev, [index]: true }))
        await apinode.delete(`/loja/pedidos/${id}/${localStorage.idconta}`)
        await fetchData()
        toast.success('Deletado com sucesso')
      } catch (err) {
        sendErrorApiMessage(err, 'Erro ao deletar')
      } finally {
        setIsDeleteLoading((prev) => ({ ...prev, [index]: false }))
      }
    }
  }

  // ── Alterar status ─────────────────────────────────────────────────────────

  const openStatusModal = (item) => {
    setStatusModalId(item.id)
    setNovoStatus(item.status ?? '')
    setStatusModalOpen(true)
  }

  const handleConfirmarStatus = async () => {
    try {
      setIsStatusLoading(true)

      await apinode.patch(
        `/loja/pedidos/${statusModalId}/status/${localStorage.idconta}`,
        { id_status: novoStatus, data_armazenado: form.dataArmazenado },
      )

      toast.success('Status alterado com sucesso')
      await fetchData()
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao alterar status')
    } finally {
      setIsStatusLoading(false)
      setStatusModalOpen(false)
    }
  }

  // ── Produtos do pedido ─────────────────────────────────────────────────────

  const addProduto = () => {
    setProdutos((prev) => [...prev, { ...emptyProduto }])
  }

  const updateProduto = (index, field, value) => {
    setProdutos((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    )
  }

  const removeProduto = (index) => {
    setProdutos((prev) => prev.filter((_, i) => i !== index))
  }

  useEffect(() => {
    fetchData()
    fetchOptions()
    fetchFornecedores()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (state?.shouldOpenModal) {
      setModalOpen(true)
    }
  }, [state?.shouldOpenModal])

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Navbar disableExtraDiv />

      <div
        className="mt-page"
        style={{
          display: 'flex',
          height: '100vh',
          background: '#f5f4f0',
          fontSize: 14,
          flex: 1,
        }}
      >
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ContentLoading
            isLoading={isFetchLoading}
            loadingText="Carregando pedidos..."
          >
            <div style={{ padding: 28, flex: 1 }}>
              {/* Page header */}
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 600,
                  color: '#1a1a1a',
                  marginBottom: 4,
                }}
              >
                Pedidos
              </div>

              <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
                Cadastre, edite e gerencie os pedidos da loja.
              </div>

              {/* Filtros */}
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #e8e6e0',
                  borderRadius: 10,
                  padding: '20px 20px 16px',
                  marginBottom: 24,
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: 16,
                    marginBottom: 14,
                  }}
                >
                  <div>
                    <div style={fieldLabel}>Cliente</div>
                    <input
                      type="text"
                      placeholder="Buscar cliente..."
                      value={filtros.cliente}
                      onChange={(e) =>
                        setFiltros((f) => ({ ...f, cliente: e.target.value }))
                      }
                      style={fieldInput}
                    />
                  </div>

                  <div>
                    <div style={fieldLabel}>Cidade</div>
                    <input
                      type="text"
                      placeholder="Cidade..."
                      value={filtros.cidade}
                      onChange={(e) =>
                        setFiltros((f) => ({ ...f, cidade: e.target.value }))
                      }
                      style={fieldInput}
                    />
                  </div>

                  <div>
                    <div style={fieldLabel}>Ped. Loja ou Matriz</div>
                    <input
                      type="text"
                      placeholder="Buscar pedido..."
                      value={filtros.pedido}
                      onChange={(e) =>
                        setFiltros((f) => ({ ...f, pedido: e.target.value }))
                      }
                      style={fieldInput}
                    />
                  </div>

                  <div>
                    <div style={fieldLabel}>Status</div>
                    <select
                      value={filtros.status}
                      onChange={(e) =>
                        setFiltros((f) => ({ ...f, status: e.target.value }))
                      }
                      style={fieldSelect}
                    >
                      <option value="">Todos</option>
                      {statusOptions.map((s) => (
                        <option
                          key={s.id}
                          value={s.id}
                        >
                          {s.nome}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div style={fieldLabel}>Data Início</div>
                    <input
                      type="date"
                      value={filtros.dataInicio}
                      onChange={(e) =>
                        setFiltros((f) => ({
                          ...f,
                          dataInicio: e.target.value,
                        }))
                      }
                      style={fieldInput}
                    />
                  </div>

                  <div>
                    <div style={fieldLabel}>Data Fim</div>
                    <input
                      type="date"
                      value={filtros.dataFim}
                      onChange={(e) =>
                        setFiltros((f) => ({ ...f, dataFim: e.target.value }))
                      }
                      style={fieldInput}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={fetchData}
                    style={{
                      margin: 0,
                      background: '#2d2d2d',
                      color: '#fff',
                      border: 'none',
                      padding: '9px 20px',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Buscar
                  </button>

                  <button
                    onClick={openNew}
                    style={{
                      margin: 0,
                      background: '#c8a030',
                      color: '#fff',
                      border: 'none',
                      padding: '9px 18px',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Plus />
                    Novo
                  </button>
                </div>
              </div>

              {/* Table */}
              <div
                style={{
                  background: '#fff',
                  borderRadius: 10,
                  border: '1px solid #e8e6e0',
                }}
              >
                <table>
                  <thead>
                    <tr style={{ background: '#fff' }}>
                      {[
                        { value: 'idpedidoloja', label: 'Ped. Loja' },
                        { value: 'pedido_matriz', label: 'Ped. Matriz' },
                        { value: 'cliente', label: 'Cliente' },
                        { value: 'data_pedido', label: 'Data' },
                        { value: 'data_entrega', label: 'Entrega' },
                        { value: 'cidade', label: 'Cidade' },
                        { value: 'status_nome', label: 'Status' },
                        { value: 'data_armazenado', label: 'Armazenado Há' },
                      ].map((h) => (
                        <Th
                          key={h.value}
                          label={h.label}
                          value={h.value}
                          style={{
                            padding: '11px 14px',
                            textAlign: 'left',
                            fontSize: 11,
                            fontWeight: 600,
                            color: '#999',
                            letterSpacing: '0.8px',
                            textTransform: 'uppercase',
                            borderBottom: '1px solid #e8e6e0',
                            whiteSpace: 'nowrap',
                            background: '#ffffff',
                          }}
                        />
                      ))}

                      <th
                        style={{
                          padding: '11px 14px',
                          textAlign: 'left',
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#999',
                          letterSpacing: '0.8px',
                          textTransform: 'uppercase',
                          borderBottom: '1px solid #e8e6e0',
                          whiteSpace: 'nowrap',
                          background: '#ffffff',
                        }}
                      >
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item, index) => (
                      <tr
                        key={item.id}
                        style={{
                          borderBottom:
                            index < filteredItems.length - 1
                              ? '1px solid #f0ede8'
                              : 'none',
                        }}
                      >
                        <td
                          style={{
                            padding: '14px 14px',
                            color: '#2a2a2a',
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          {item.idpedidoloja}
                        </td>
                        <td
                          style={{
                            padding: '14px 14px',
                            color: '#555',
                            fontSize: 13,
                          }}
                        >
                          {item.pedido_matriz}
                        </td>
                        <td
                          style={{
                            padding: '14px 14px',
                            color: '#2a2a2a',
                            fontSize: 13,
                            maxWidth: 180,
                          }}
                        >
                          {item.cliente}
                        </td>
                        <td
                          style={{
                            padding: '14px 14px',
                            color: '#555',
                            fontSize: 13,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.data_pedido
                            ? new Date(item.data_pedido).toLocaleDateString(
                                'pt-BR',
                              )
                            : '—'}
                        </td>
                        <td
                          style={{
                            padding: '14px 14px',
                            color: '#555',
                            fontSize: 13,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.data_entrega
                            ? new Date(item.data_entrega).toLocaleDateString(
                                'pt-BR',
                              )
                            : '—'}
                        </td>
                        <td
                          style={{
                            padding: '14px 14px',
                            color: '#555',
                            fontSize: 13,
                          }}
                        >
                          {item.cidade ?? '—'}
                        </td>
                        <td style={{ padding: '14px 14px' }}>
                          {item.status_nome ? (
                            <StatusBadge label={item.status_nome} />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td
                          style={{
                            padding: '14px 14px',
                            color: item.data_armazenado ? '#c8a030' : '#aaa',
                            fontSize: 13,
                            fontWeight: item.data_armazenado ? 600 : 400,
                          }}
                        >
                          {item.data_armazenado
                            ? `${differenceInDays(
                                new Date(),
                                new Date(item.data_armazenado),
                              )} ${
                                differenceInDays(
                                  new Date(),
                                  new Date(item.data_armazenado),
                                ) > 1
                                  ? 'dias'
                                  : 'dia'
                              }`
                            : '—'}
                        </td>
                        <td style={{ padding: '14px 14px' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {/* Editar */}
                            <button
                              onClick={() => openEdit(item.id)}
                              title="Editar"
                              style={{
                                margin: 0,
                                borderRadius: 7,
                                border: 'none',
                                cursor: 'pointer',
                                background: '#f0ede8',
                                color: '#444',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <IconEdit />
                            </button>
                            {/* Alterar Status */}
                            <button
                              onClick={() => openStatusModal(item)}
                              title="Alterar Status"
                              style={{
                                margin: 0,
                                borderRadius: 7,
                                border: 'none',
                                cursor: 'pointer',
                                background: '#d1fae5',
                                color: '#065f46',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <IconCheck />
                            </button>

                            {/* Deletar */}
                            <button
                              onClick={() => handleDelete(item.id, index)}
                              title="Deletar"
                              style={{
                                margin: 0,
                                borderRadius: 7,
                                border: 'none',
                                cursor: 'pointer',
                                background: '#fee2e2',
                                color: '#dc2626',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {isDeleteLoading[index] ? (
                                <CustomSpinner
                                  size={14}
                                  color="#dc2626"
                                />
                              ) : (
                                <IconTrash />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {pedidosList.length === 0 && (
                      <tr>
                        <td
                          colSpan={9}
                          style={{
                            padding: '32px 20px',
                            textAlign: 'center',
                            color: '#aaa',
                            fontSize: 13,
                          }}
                        >
                          Nenhum pedido encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Paginate />
            </div>
          </ContentLoading>
        </div>

        {/* ── Modal Novo/Editar Pedido ─────────────────────────────────────────── */}
        {modalOpen && (
          <div
            onClick={(e) => {
              if (e.target === e.currentTarget) setModalOpen(false)
            }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              zIndex: 1001,
              overflowY: 'auto',
              padding: '40px 0',
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                width: '90%',
                // maxWidth: 900,
                padding: 32,
                boxShadow: '0 8px 40px rgba(0,0,0,0.16)',
                margin: 'auto',
              }}
            >
              <ContentLoading isLoading={isFetchItemLoading}>
                {/* Header */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 28,
                  }}
                >
                  <span
                    style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a' }}
                  >
                    {editId ? 'Editar Pedido' : 'Novo Pedido'}
                  </span>
                  <button
                    onClick={() => setModalOpen(false)}
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 7,
                      border: 'none',
                      background: '#f0ede8',
                      color: '#555',
                      cursor: 'pointer',
                      fontSize: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Buscar pedido existente */}
                <div
                  style={{
                    background: '#f8f7f3',
                    border: '1px solid #e8e6e0',
                    borderRadius: 10,
                    padding: '16px 18px',
                    marginBottom: 24,
                  }}
                >
                  <div style={{ ...fieldLabel, marginBottom: 10 }}>
                    Buscar Pedido Existente
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input
                      type="text"
                      placeholder="Digite o nº Pedido Loja ou Pedido Matriz..."
                      value={buscaPedido}
                      onChange={(e) => setBuscaPedido(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleBuscarPedido()
                      }}
                      style={{ ...fieldInput, flex: 1 }}
                    />

                    <button
                      onClick={handleBuscarPedido}
                      disabled={isBuscaLoading}
                      style={{
                        margin: 0,
                        background: '#2d2d2d',
                        color: '#fff',
                        border: 'none',
                        padding: '9px 20px',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      {isBuscaLoading ? <CustomSpinner size={13} /> : 'Buscar'}
                    </button>
                  </div>
                </div>

                {/* Row: Pedido Loja | Pedido Matriz | Status */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr',
                    gap: 16,
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <div style={fieldLabel}>Pedido Loja (idpedidoloja)</div>
                    <input
                      type="text"
                      placeholder="Ex: 1234"
                      value={form.idpedidoloja}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, idpedidoloja: e.target.value }))
                      }
                      style={fieldInput}
                    />
                  </div>
                  <div>
                    <div style={fieldLabel}>Pedido Matriz</div>
                    <input
                      type="text"
                      placeholder="Ex: 5678"
                      value={form.pedido_matriz}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          pedido_matriz: e.target.value,
                        }))
                      }
                      style={fieldInput}
                    />
                  </div>

                  <div>
                    <div style={fieldLabel}>Status</div>
                    <select
                      value={form.status}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, status: e.target.value }))
                      }
                      style={fieldSelect}
                    >
                      <option value="">Selecionar...</option>
                      {statusOptions.map((s) => (
                        <option
                          key={s.id}
                          value={s.id}
                        >
                          {s.nome}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div style={fieldLabel}>Fornecedor</div>
                    <select
                      value={form.fornecedor}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, fornecedor: e.target.value }))
                      }
                      style={fieldSelect}
                    >
                      <option value="">Selecionar...</option>
                      {fornecedoresOptions.map((s) => (
                        <option
                          key={s.id}
                          value={s.id}
                        >
                          {s.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Cliente */}
                <div style={{ marginBottom: 16 }}>
                  <div style={fieldLabel}>Cliente</div>
                  <input
                    type="text"
                    placeholder="Nome do cliente"
                    value={form.cliente}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, cliente: e.target.value }))
                    }
                    style={fieldInput}
                  />
                </div>

                {/* Row: Data Pedido | Data Entrega | Data Armazenado | Cidade */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr',
                    gap: 16,
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <div style={fieldLabel}>Data do Pedido</div>
                    <input
                      type="date"
                      value={form.dataPedido}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, dataPedido: e.target.value }))
                      }
                      style={fieldInput}
                    />
                  </div>
                  <div>
                    <div style={fieldLabel}>Data de Entrega</div>
                    <input
                      type="date"
                      value={form.dataEntrega}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, dataEntrega: e.target.value }))
                      }
                      style={fieldInput}
                    />
                  </div>
                  <div>
                    <div style={fieldLabel}>Data Armazenado</div>
                    <input
                      type="date"
                      value={form.dataArmazenado}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          dataArmazenado: e.target.value,
                        }))
                      }
                      style={fieldInput}
                    />
                  </div>
                  <div>
                    <div style={fieldLabel}>Cidade</div>
                    <input
                      type="text"
                      placeholder="Cidade"
                      value={form.cidade}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, cidade: e.target.value }))
                      }
                      style={fieldInput}
                    />
                  </div>
                </div>

                {/* Observação */}
                <div style={{ marginBottom: 24 }}>
                  <div style={fieldLabel}>Observação</div>
                  <input
                    type="text"
                    placeholder="Observações sobre o pedido..."
                    value={form.observacao}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, observacao: e.target.value }))
                    }
                    style={fieldInput}
                  />
                </div>

                {/* Produtos do Pedido */}
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: '#1a1a1a',
                      }}
                    >
                      Produtos do Pedido
                    </span>
                    <button
                      onClick={addProduto}
                      style={{
                        background: '#2d2d2d',
                        color: '#f0c040',
                        border: 'none',
                        padding: '7px 14px',
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      <Plus size={13} />
                      Adicionar Item
                    </button>
                  </div>

                  <div
                    style={{
                      border: '1px solid #e8e6e0',
                      borderRadius: 10,
                      overflow: 'hidden',
                    }}
                  >
                    <table
                      style={{ width: '100%', borderCollapse: 'collapse' }}
                    >
                      <thead>
                        <tr style={{ background: '#f8f7f3' }}>
                          <th
                            style={{
                              background: '#f8f7f3',
                              width: 'auto',
                              padding: '9px 12px',
                              textAlign: 'left',
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#999',
                              letterSpacing: '0.8px',
                              textTransform: 'uppercase',
                              borderBottom: '1px solid #e8e6e0',
                            }}
                          >
                            Código
                          </th>

                          <th
                            style={{
                              background: '#f8f7f3',
                              width: 'auto',
                              padding: '9px 12px',
                              textAlign: 'left',
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#999',
                              letterSpacing: '0.8px',
                              textTransform: 'uppercase',
                              borderBottom: '1px solid #e8e6e0',
                            }}
                          >
                            Descrição
                          </th>

                          <th
                            style={{
                              background: '#f8f7f3',
                              width: 100,
                              padding: '9px 12px',
                              textAlign: 'left',
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#999',
                              letterSpacing: '0.8px',
                              textTransform: 'uppercase',
                              borderBottom: '1px solid #e8e6e0',
                            }}
                          >
                            Qtd
                          </th>

                          <th
                            style={{
                              background: '#f8f7f3',
                              width: 'auto',
                              padding: '9px 12px',
                              textAlign: 'left',
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#999',
                              letterSpacing: '0.8px',
                              textTransform: 'uppercase',
                              borderBottom: '1px solid #e8e6e0',
                            }}
                          >
                            Substatus
                          </th>

                          <th
                            style={{
                              background: '#f8f7f3',
                              width: 'auto',
                              padding: '9px 12px',
                              textAlign: 'left',
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#999',
                              letterSpacing: '0.8px',
                              textTransform: 'uppercase',
                              borderBottom: '1px solid #e8e6e0',
                            }}
                          >
                            Obs
                          </th>

                          <th
                            style={{
                              background: '#f8f7f3',
                              width: 'auto',
                              padding: '9px 12px',
                              textAlign: 'left',
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#999',
                              letterSpacing: '0.8px',
                              textTransform: 'uppercase',
                              borderBottom: '1px solid #e8e6e0',
                            }}
                          ></th>
                        </tr>
                      </thead>
                      <tbody>
                        {produtos.map((prod, index) => (
                          <tr
                            key={index}
                            style={{
                              borderBottom:
                                index < produtos.length - 1
                                  ? '1px solid #f0ede8'
                                  : 'none',
                            }}
                          >
                            <td style={{ padding: '8px 10px', width: 90 }}>
                              <input
                                type="text"
                                placeholder="Cód"
                                value={prod.codigo}
                                onChange={(e) =>
                                  updateProduto(index, 'codigo', e.target.value)
                                }
                                style={{ ...fieldInput, padding: '6px 10px' }}
                                onBlur={() =>
                                  handleSearchItem(prod.codigo, index)
                                }
                              />
                            </td>
                            <td
                              style={{
                                padding: '8px 10px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                              }}
                            >
                              {isSearchItemLoading[index] && (
                                <CustomSpinner size={15} />
                              )}

                              <input
                                type="text"
                                disabled={isSearchItemLoading[index]}
                                placeholder="Descrição"
                                value={prod.descricao}
                                onChange={(e) =>
                                  updateProduto(
                                    index,
                                    'descricao',
                                    e.target.value,
                                  )
                                }
                                style={{ ...fieldInput, padding: '6px 10px' }}
                              />
                            </td>
                            <td style={{ padding: '8px 10px', width: 70 }}>
                              <input
                                type="number"
                                placeholder="0"
                                value={prod.qtd}
                                onChange={(e) =>
                                  updateProduto(index, 'qtd', e.target.value)
                                }
                                style={{ ...fieldInput, padding: '6px 10px' }}
                              />
                            </td>
                            <td style={{ padding: '8px 10px', width: 160 }}>
                              <select
                                value={prod.substatus}
                                onChange={(e) =>
                                  updateProduto(
                                    index,
                                    'substatus',
                                    e.target.value,
                                  )
                                }
                                style={{ ...fieldSelect, padding: '6px 10px' }}
                              >
                                <option value="">—</option>
                                {substatusOptions.map((s) => (
                                  <option
                                    key={s.id}
                                    value={s.id}
                                  >
                                    {s.nome}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td style={{ padding: '8px 10px' }}>
                              <input
                                type="text"
                                placeholder="Obs"
                                value={prod.obs}
                                onChange={(e) =>
                                  updateProduto(index, 'obs', e.target.value)
                                }
                                style={{ ...fieldInput, padding: '6px 10px' }}
                              />
                            </td>
                            <td style={{ padding: '8px 10px', width: 44 }}>
                              <button
                                onClick={() => removeProduto(index)}
                                style={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: 7,
                                  border: 'none',
                                  cursor: 'pointer',
                                  background: '#fee2e2',
                                  color: '#dc2626',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                        {produtos.length === 0 && (
                          <tr>
                            <td
                              colSpan={6}
                              style={{
                                padding: '20px',
                                textAlign: 'center',
                                color: '#aaa',
                                fontSize: 13,
                              }}
                            >
                              Nenhum produto adicionado.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Footer */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 10,
                  }}
                >
                  <button
                    onClick={() => setModalOpen(false)}
                    style={{
                      margin: 0,
                      background: 'transparent',
                      border: '1px solid #ddd',
                      color: '#555',
                      padding: '9px 20px',
                      borderRadius: 8,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    style={{
                      margin: 0,
                      background: '#c8a030',
                      color: '#fff',
                      border: 'none',
                      padding: '9px 22px',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                    }}
                  >
                    {isUpdateLoading ? (
                      <CustomSpinner size={13} />
                    ) : (
                      <>
                        <FloppyDisk size={15} />
                        Salvar Pedido
                      </>
                    )}
                  </button>
                </div>
              </ContentLoading>
            </div>
          </div>
        )}

        {/* ── Modal Alterar Status ─────────────────────────────────────────────── */}
        {statusModalOpen && (
          <div
            onClick={(e) => {
              if (e.target === e.currentTarget) setStatusModalOpen(false)
            }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 200,
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                width: 440,
                padding: 28,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 24,
                }}
              >
                <span
                  style={{ fontSize: 17, fontWeight: 600, color: '#1a1a1a' }}
                >
                  Alterar Status
                </span>
                <button
                  onClick={() => setStatusModalOpen(false)}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 7,
                    border: 'none',
                    background: '#f0ede8',
                    color: '#555',
                    cursor: 'pointer',
                    fontSize: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: 24 }}>
                <div style={fieldLabel}>Novo Status</div>
                <select
                  value={novoStatus}
                  onChange={(e) => setNovoStatus(e.target.value)}
                  style={fieldSelect}
                >
                  <option value="">Selecionar...</option>
                  {statusOptions.map((s) => (
                    <option
                      key={s.id}
                      value={s.id}
                    >
                      {s.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}
              >
                <button
                  onClick={() => setStatusModalOpen(false)}
                  style={{
                    background: 'transparent',
                    border: '1px solid #ddd',
                    color: '#555',
                    padding: '9px 20px',
                    borderRadius: 8,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmarStatus}
                  disabled={!novoStatus}
                  style={{
                    background: '#c8a030',
                    color: '#fff',
                    border: 'none',
                    padding: '9px 22px',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {isStatusLoading ? <CustomSpinner size={13} /> : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
